<template>
  <v-container class="mb-10">
    <div class="d-flex align-center justify-space-between">
      <h1>Wallet</h1>
      <v-btn
        icon
        :loading="gettingWallet"
        title="Refresh balance"
        @click="getWalletData"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-overlay opacity="0.8" :value="gettingWallet">
      <v-progress-circular indeterminate></v-progress-circular>
    </v-overlay>
    <v-row>
      <v-col cols="12" md="12">
        <v-card elevation="3" color="primary" rounded="lg" class="pa-2">
          <v-card-text class="white--text">
            <p class="overline white--text opacity-80 mb-0">
              Your {{ getCurrencyCode(user.country) }} Wallet
            </p>
            <span class="opacity-85">Available Balance</span>
            <div class="flex items-center">
              <h4 v-if="showAmount" class="text-h4">
                {{ userCurrency }} {{ numberWithCommas(wallet.balance) }}
              </h4>
              <h4 v-else class="text-h4">*********</h4>
              <span
                class="ml-5 cursor-auto"
                @click="
                  showAmount = !showAmount
                  localStorage.setItem('showAmount', showAmount)
                "
              >
                <v-icon v-if="showAmount" size="32" color="white"
                  >mdi-eye</v-icon
                >
                <v-icon v-else size="32" color="white">mdi-eye-off</v-icon>
              </span>
            </div>
          </v-card-text>
          <v-spacer></v-spacer>
          <v-card-actions class="d-block d-sm-flex">
            <v-spacer class="hidden-sm-and-down"></v-spacer>
            <v-btn
              depressed
              to="/user/wallet/withdraw"
              class="mt-2 mx-2 text-center"
            >
              Withdraw
            </v-btn>
            <!--            <v-btn-->
            <!--              depressed-->
            <!--              to="/user/wallet/fund"-->
            <!--              class="mt-2 mx-2 text-center"-->
            <!--              v-if="userCountry === COUNTRIES.NG"-->
            <!--            >-->
            <!--              Fund-->
            <!--            </v-btn>-->
            <v-btn
              depressed
              to="/user/wallet/snappy-gift"
              class="mt-2 mx-2 text-center"
            >
              SnappyGift
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Wallet history -->
    <v-row>
      <v-col cols="12">
        <v-card elevation="1" rounded="lg" class="mt-4">
          <v-card-title class="text-h6">Wallet Transactions</v-card-title>
          <v-data-table
            :headers="historyHeaders"
            :items="historyRows"
            :items-per-page="perPage"
            :loading="loadingHistory"
            :server-items-length="historyTotal"
            :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
            @update:items-per-page="updateItemsPerPage"
            @update:page="updatePage"
            @click:row="openHistoryDetails"
          >
            <template v-slot:no-data>
              <div class="py-6 text-center grey--text">
                No wallet transactions yet.
              </div>
            </template>

            <template v-slot:[`item.createdAt`]="{ item }">
              <span>{{ formatHistoryDate(item.createdAt) }}</span>
            </template>

            <template v-slot:[`item.type`]="{ item }">
              <span
                class="text-capitalize font-weight-medium"
                :class="item.type === 'credit' ? 'success--text' : 'error--text'"
              >
                {{ item.type }}
              </span>
            </template>

            <template v-slot:[`item.amount`]="{ item }">
              <span
                :class="item.type === 'credit' ? 'success--text' : 'error--text'"
              >
                {{ item.type === 'credit' ? '+' : '-'
                }}{{ userCurrency }}
                {{ numberWithCommas(Number(item.amount || 0)) }}
              </span>
            </template>

            <template v-slot:[`item.fee`]="{ item }">
              <span v-if="item.fee > 0">
                {{ userCurrency }} {{ numberWithCommas(item.fee) }}
              </span>
              <span v-else class="grey--text">&mdash;</span>
            </template>

            <template v-slot:[`item.balance`]="{ item }">
              <span>{{ userCurrency }} {{ numberWithCommas(Number(item.balance || 0)) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="historyDialog" max-width="480">
      <v-card v-if="selectedHistory">
        <v-card-title class="d-flex align-center justify-space-between">
          <span class="text-capitalize">
            {{ selectedHistory.category || selectedHistory.type }}
          </span>
          <v-btn icon small @click="historyDialog = false" aria-label="Close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-for="row in historyDetailRows"
            :key="row.label"
            class="d-flex justify-space-between py-2"
          >
            <span class="text-body-2 grey--text text--darken-1">
              {{ row.label }}
            </span>
            <span class="text-body-2 font-weight-medium text-right">
              {{ row.value }}
            </span>
          </div>
          <div
            v-if="selectedHistory.ref"
            class="d-flex justify-space-between align-center py-2"
          >
            <span class="text-body-2 grey--text text--darken-1">Reference</span>
            <span class="d-flex align-center">
              <span class="text-body-2 font-weight-medium mr-2">
                {{ selectedHistory.ref }}
              </span>
              <v-btn icon small @click="copy(selectedHistory.ref)">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import walletApi from '../../../api/users/wallet'
import { mapState } from 'vuex'
import { numberWithCommas } from '../../../utils/helpers'
import { COUNTRIES, getCurrencyCode } from '../../../utils/countryHelpers'

export default {
  data: () => ({
    COUNTRIES,
    getCurrencyCode,
    numberWithCommas,
    cashbackDialog: false,
    convertDialog: false,
    banks: 0,
    loadingConvert: false,
    gettingWallet: false,
    amount: '',
    loadingConvertCashback: false,
    cashbackAmount: '',
    showAmount: false,
    history: [],
    historyTotal: 0,
    loadingHistory: false,
    perPage: 10,
    currentPage: 1,
    historyDialog: false,
    selectedHistory: null,
    historyHeaders: [
      { text: 'Date', value: 'createdAt' },
      { text: 'Type', value: 'type', sortable: false },
      { text: 'Description', value: 'description', sortable: false },
      { text: 'Amount', value: 'amount', sortable: false },
      { text: 'Fee', value: 'fee', sortable: false },
      { text: 'Balance After', value: 'balance', sortable: false }
    ]
  }),
  components: {},
  computed: {
    ...mapState({
      wallet: (state) => state.user.wallet,

      user: (state) => state.user.user,
      siteSettings: (state) => state.user.siteSettings,
      countrySetting: (state) => state.user.countryCurrency
    }),
    historyRows() {
      return this.history.map((item) => ({
        ...item,
        description: item.description || item.transactionType || item.category,
        // `amount` is already net of the fee, so it gets its own column.
        fee: Number(item.fee || 0)
      }))
    },
    historyDetailRows() {
      const item = this.selectedHistory
      if (!item) return []
      const money = (value) =>
        `${this.userCurrency} ${numberWithCommas(Number(value || 0))}`

      const rows = [
        { label: 'Type', value: item.type },
        { label: 'Amount', value: money(item.amount) }
      ]
      if (item.fee > 0) {
        rows.push({ label: 'Fee', value: money(item.fee) })
      }
      rows.push({ label: 'Balance after', value: money(item.balance) })
      if (item.previousBalance != null) {
        rows.push({ label: 'Balance before', value: money(item.previousBalance) })
      }
      if (item.description) {
        rows.push({ label: 'Description', value: item.description })
      }
      rows.push({ label: 'Date', value: this.formatHistoryDate(item.createdAt) })
      return rows
    }
  },
  created() {
    this.$store.dispatch('user/getSiteSettings')
    this.$store.dispatch('user/getCountryCurrency')
    this.getWalletData()
  },
  mounted() {
    const showAmount = localStorage.getItem('showAmount')
    if (showAmount) {
      this.showAmount = showAmount
    } else {
      this.showAmount = false
      localStorage.setItem('showAmount', false)
    }
  },
  methods: {
    copy(copy) {
      const input = document.createElement('input')
      input.value = copy

      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$store.dispatch('alert', { message: 'Copied!', time: 2000 })
    },

    async getWalletData() {
      this.gettingWallet = true
      const res = await walletApi.data().getWallet()
      this.$store.commit('user/setWalletData', res.data.data)
      this.gettingWallet = false
      this.getWalletHistory()
    },
    async getWalletHistory() {
      this.loadingHistory = true
      const res = await walletApi.data().walletHistory({
        limit: this.perPage,
        offset: (this.currentPage - 1) * this.perPage,
        orderBy: 'createdAt',
        order: 'desc'
      })
      this.loadingHistory = false

      if (res.error) {
        this.$store.dispatch('alert', {
          message:
            res.errorMessage?.message || 'Unable to load wallet transactions',
          error: true,
          status: true
        })
        return
      }

      // The endpoint answers { data: { data: [...], meta } }.
      const payload = res.data.data
      this.history = Array.isArray(payload) ? payload : payload?.data ?? []
      this.historyTotal = payload?.meta?.total ?? this.history.length
    },
    updatePage(page) {
      this.currentPage = page
      this.getWalletHistory()
    },
    updateItemsPerPage(count) {
      this.perPage = count
      this.currentPage = 1
      this.getWalletHistory()
    },
    openHistoryDetails(item) {
      this.selectedHistory = item
      this.historyDialog = true
    },
    formatHistoryDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleString()
    }
  }
}
</script>

<style>
.bg-gray-100 {
  background-color: #f5f5f5;
}
</style>
